import { useEffect, useState, useMemo } from "react";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
} from "@chakra-ui/modal";
import { MdAdd, MdDelete } from "react-icons/md";

import Card from "components/card";
import InputField from "components/fields/InputField";
import { generateCreditNoteFromScratch } from "interfaces/invoice";
import { getPricing } from "interfaces/pricing";
import SearchSelector from "components/fields/SearchSelector";
import { getCustomers } from "interfaces/customer";
import { getProducts } from "interfaces/products";

interface ProductLine {
  productName: string | null;
  productRef: string | null;
  quantity: number;
  vat: number;
  price: number;
  accountingCode?: string | null;
}

const NewCreditNoteModal = (props: { isOpen: any; onClose: any }) => {
  const { isOpen, onClose } = props;
  const [products, setProducts] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [customerPricings, setCustomerPricings] = useState<any[]>([]);
  const [creditNote, setCreditNote] = useState({
    customerId: null as string | null,
    note: null as string | null,
    comment: null as string | null,
    products: [] as ProductLine[],
  });

  const initCustomers = async () => {
    const res = await getCustomers();
    const newCustomers = res.data.map((customer: any) => ({
      ...customer,
      company: customer.displayName || customer.company,
    }));
    if (res) setCustomers(newCustomers);
  };

  const initProducts = async () => {
    const res = await getProducts(1, 10000);
    if (res) setProducts(res.data);
  };

  const fetchCustomerPricings = async (customerId: string) => {
    const res = await getPricing(customerId, 1, 1000);
    if (res?.data) setCustomerPricings(res.data);
    else setCustomerPricings([]);
  };

  const handleClose = () => {
    onClose();
    setCreditNote({
      customerId: null,
      note: null,
      comment: null,
      products: [],
    });
    setCustomerPricings([]);
  };

  const generateCreditNote = async () => {
    const res = await generateCreditNoteFromScratch(creditNote);
    if (res.data) {
      onClose();
      setCreditNote({
        customerId: null,
        note: null,
        comment: null,
        products: [],
      });
      window.location.reload();
    }
  };

  useEffect(() => {
    initCustomers();
    initProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Line products ---
  const addProductLine = () => {
    setCreditNote({
      ...creditNote,
      products: [
        ...creditNote.products,
        { productName: null, productRef: null, quantity: 1, vat: 5.5, price: 0, accountingCode: null },
      ],
    });
  };

  const removeProductLine = (index: number) => {
    const newProducts = [...creditNote.products];
    newProducts.splice(index, 1);
    setCreditNote({ ...creditNote, products: newProducts });
  };

  const updateProductLine = <K extends keyof ProductLine>(
    index: number,
    field: K,
    value: ProductLine[K]
  ) => {
    setCreditNote((prev) => {
      const updatedProducts = [...prev.products];
      updatedProducts[index] = { ...updatedProducts[index], [field]: value };
      return { ...prev, products: updatedProducts };
    });
  };

  const totals = useMemo(() => {
       const safeNum = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);
       const ht = creditNote.products.reduce((acc, p) => {
         return acc + safeNum(p.quantity) * safeNum(p.price);
       }, 0);
       const tva = creditNote.products.reduce((acc, p) => {
         return acc + safeNum(p.quantity) * safeNum(p.price) * (safeNum(p.vat) / 100);
       }, 0);
       return {
         ht,
         tva,
         ttc: ht + tva,
       };
     }, [creditNote.products]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay className="bg-[#000] !opacity-30" />
      <ModalContent className="!z-[1002] !m-auto !w-max min-w-[350px] !max-w-[85%] md:top-[12vh]">
        <ModalBody>
          <Card extra="px-[30px] pt-[35px] pb-[40px] max-w-[750px] flex flex-col !z-[1004] max-h-[80vh]">
            <h1 className="mb-1 text-2xl font-bold shrink-0">Créer un avoir</h1>

            <div className="overflow-y-auto flex-1 pr-1">
            {/* Customers */}
            <div className="z-50 col-span-3">
              <SearchSelector
                label="Client*"
                displayKey="company"
                options={customers}
                onSelect={(e: any) => {
                  setCreditNote((prev: any) => ({
                    ...prev,
                    customerId: e._id,
                  }));
                  fetchCustomerPricings(e._id);
                }}
                inputSmall={true}
              />
            </div>

            {/* Note  */}
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-700">Note <span className="text-xs text-gray-400">(affiché après la référence sur l'avoir)</span></label>
              <input
                type="text"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-toola-500 focus:outline-none"
                placeholder="Ex : Remboursement suite retour marchandise..."
                value={creditNote.note || ""}
                onChange={(e) =>
                  setCreditNote((prev) => ({ ...prev, note: e.target.value || null }))
                }
              />
            </div>

            {/* product lines */}
          <div className="mt-8 flex flex-col gap-4">
            {creditNote.products.map((prod, index) => (
              <div key={index} className="flex gap-2 items-start">
                <div className="flex-1 min-w-[220px]">
                  <SearchSelector
                    label="Produit*"
                    displayKey="name"
                    secondaryKey="ref"
                    options={products}
                    onSelect={(e: any) => {
                      updateProductLine(index, "productName", e.name);
                      updateProductLine(index, "productRef", e.ref);
                      updateProductLine(index, "accountingCode", e.accountingCode || null);
                      // Check for customer-specific pricing
                      const customPricing = customerPricings.find(
                        (p: any) => p.productId === e._id || p.productId?.toString() === e._id?.toString()
                      );
                      const priceToApply = customPricing ? customPricing.price : (typeof e?.price !== 'undefined' ? Number(e.price) || 0 : 0);
                      updateProductLine(index, "price", priceToApply);
                      if (typeof e?.vat !== 'undefined') {
                        updateProductLine(index, "vat", Number(e.vat) || 0);
                      }
                    }}
                    inputSmall={true}
                  />
                </div>
                <div className="w-28">
                  <InputField
                    id={`quantity-${index}`}
                    label="Quantité*"
                    type="number"
                    value={prod.quantity}
                    onChange={(e: any) =>
                      updateProductLine(index, "quantity", Number(e.target.value))
                    }
                    inputSmall={true}
                  />
                </div>
                <div className="w-32">
                  <InputField
                    id={`price-${index}`}
                    label="Prix HT"
                    type="number"
                    value={prod.price}
                    onChange={(e: any) =>
                      updateProductLine(index, "price", Number(e.target.value))
                    }
                    inputSmall={true}
                  />
                </div>
                <div className="w-28">
                  <InputField
                    id={`vat-${index}`}
                    label="TVA (%)"
                    type="number"
                    value={prod.vat}
                    onChange={(e: any) =>
                      updateProductLine(index, "vat", Number(e.target.value))
                    }
                    inputSmall={true}
                  />
                </div>

                  <button
                    className="p-2 text-red-500 hover:text-red-700"
                    onClick={() => removeProductLine(index)}
                  >
                    <MdDelete size={22} />
                  </button>
                </div>
              ))}
            <div className="self-start pt-6">
              <button
                className="flex items-center gap-1 text-toola-500 font-medium hover:underline"
                onClick={addProductLine}
              >
                <MdAdd /> Ajouter une ligne produit
              </button>
            </div>
          </div>

            {/* Comment (collapsible, added on all product lines) */}
            <div className="mt-4">
              <button
                type="button"
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                onClick={() => setCreditNote((prev) => ({ ...prev, comment: prev.comment === null ? '' : null }))}
              >
                <MdAdd size={14} className={creditNote.comment !== null ? 'rotate-45 transition-transform' : 'transition-transform'} />
                Ajouter un commentaire sur les lignes
              </button>
              {creditNote.comment !== null && (
                <input
                  type="text"
                  className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-800 placeholder-gray-400 focus:border-toola-500 focus:outline-none"
                  placeholder="Commentaire sur les lignes de l'avoir..."
                  value={creditNote.comment || ""}
                  onChange={(e) =>
                    setCreditNote((prev) => ({ ...prev, comment: e.target.value || '' }))
                  }
                />
              )}
            </div>
              <div className="mt-6 grid grid-cols-3 gap-3 max-w-sm ml-auto">

                <div className="col-span-2 text-right text-sm text-gray-600">Total HT</div>
                <div className="text-right font-medium">
                  {totals.ht.toFixed(2)} €
                </div>
                <div className="col-span-2 text-right text-sm text-gray-600">TVA</div>
                <div className="text-right font-medium">
                  {totals.tva.toFixed(2)} €
                </div>
                <div className="col-span-2 text-right text-sm text-gray-800">Total TTC</div>
                <div className="text-right font-semibold text-gray-900">
                  {totals.ttc.toFixed(2)} €
                </div>
              </div>
            </div>
            {/* Buttons */}
            <div className="mt-5 flex justify-between gap-2 shrink-0">
              <button
                onClick={handleClose}
                className="linear rounded-xl border-2 border-gray-700 px-5 py-3 text-base font-medium text-gray-700 transition duration-200 hover:bg-red-600/5 active:bg-red-700/5 dark:border-red-400 dark:bg-red-400/10 dark:text-white dark:hover:bg-red-300/10 dark:active:bg-red-200/10"
              >
                Annuler
              </button>
              {creditNote.customerId && creditNote.products.length > 0 && (
                <button
                  className="linear hover:bg-red600 ml-3 flex items-center justify-center rounded-xl bg-toola-500 px-3 py-3 text-base font-medium text-white active:bg-toola-700"
                  onClick={generateCreditNote}
                >
                  Créer la facture d'avoir
                </button>
              )}
            </div>
          </Card>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default NewCreditNoteModal;
